import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Button } from '@insurancemenu/imenu-components-lib';
import { ArrowBack } from '@insurancemenu/imenu-components-lib/dist/icons';
import { useRootStore } from 'store/rootStore';
import { observer } from 'mobx-react-lite';
import clsx from 'clsx';
import useImmutablePeriod from 'global/hooks/useImmutablePeriod';
import { ModalType } from 'global/utils/ModalsHelper';
import ActionModal from 'components/common/modals/ActionModal';
import { createStyles } from './styles';

interface Props {
  tutorialLink?: string;
  isSaveChangesModal?: boolean;
  onSaveChangesAction?: () => Promise<void>;
  backButtonFunc?: () => void;
  title?: string;
  subtitle?: React.ReactNode;
  isFormCompleted?: boolean;
  showBackButton?: boolean;
  className?: string;
}

/** @deprecated: use PageHeader from the components library instead */
const ChecklistOptionHeader = observer(
  ({
    isSaveChangesModal,
    title,
    subtitle,
    isFormCompleted,
    tutorialLink,
    showBackButton = true,
    onSaveChangesAction,
    backButtonFunc,
    className = '',
  }: Props): JSX.Element => {
    // Common data
    const history = useHistory();
    const { periodId } = useParams<{ periodId: string }>();

    const [showSaveChangesModal, setShowSaveChangesModal] = useState(false);
    // Mobx store
    const {
      exitModalStore: { showExitModal, openModal, closeModal },
    } = useRootStore();

    const isPeriodNotEditable = useImmutablePeriod(periodId);

    // Component actions
    function openSaveChangesModal(): void {
      setShowSaveChangesModal(true);
    }

    function returnBack(): void {
      if (isFormCompleted) closeModal();
      if (isSaveChangesModal && !isFormCompleted) setShowSaveChangesModal(false);
      history.goBack();
    }

    const onBackButtonClick = (): void => {
      if (isSaveChangesModal && !isFormCompleted && !isPeriodNotEditable) openSaveChangesModal();
      if (isFormCompleted && !isPeriodNotEditable) openModal();
      if ((!isSaveChangesModal && !isFormCompleted) || isPeriodNotEditable) {
        history.push(`/checklist/${periodId}`);
      }
    };

    const classes = createStyles();
    const backButtonStyle = !subtitle && !tutorialLink && classes.backButtonInLine;

    return (
      <>
        <header className={clsx(classes.header, className)}>
          {showBackButton && (
            <Button
              leftIconProp={<ArrowBack />}
              type="text"
              size="medium"
              onClick={backButtonFunc || onBackButtonClick}
              className={clsx(classes.backButton, backButtonStyle)}
            >
              Back
            </Button>
          )}
          <h1 className={classes.title}>{title}</h1>
          {subtitle && <div className={classes.caption}>{subtitle}</div>}
        </header>
        {isSaveChangesModal && onSaveChangesAction && !isFormCompleted && (
          <ActionModal
            show={showSaveChangesModal}
            onClose={returnBack}
            onAction={onSaveChangesAction}
            closeButtonText="Exit Without Saving"
            actionButtonText="Save Changes"
            title="Would you like to save your changes before exiting?"
            modalType={ModalType.NOTIFICATION}
            closable={false}
          />
        )}
        {isFormCompleted && showExitModal && (
          <ActionModal
            show={showExitModal}
            onClose={returnBack}
            onAction={closeModal}
            closeButtonText="Exit Without Saving"
            actionButtonText="Stay on this page"
            title="Would you like to exit without saving your changes?"
            modalType={ModalType.NOTIFICATION}
            closable={false}
          />
        )}
      </>
    );
  },
);

export default ChecklistOptionHeader;
